/**
 * 6-step publish flow:
 *  1. Route
 *  2. Date + time + flexibility
 *  3. Seats
 *  4. Cost (with reference cost shown)
 *  5. Preferences
 *  6. Pickup notes + confirm
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { CityDto } from '@waylo/shared';
import { createTemplateApi, listRoutes, publishRideApi } from '../lib/api';
import { useRouter } from '../lib/router';
import { useSession } from '../lib/session';
import { localeCountry, translateError, useT } from '../lib/i18n';
import { humanDate } from '../lib/datetime';
import { MainButton } from '../lib/MainButton';
import { BackButton } from '../lib/BackButton';
import { Card, CardHint, CardTitle } from '../components/Card';
import { BrandMark } from '../components/Brand';
import { TextInput, TextArea } from '../components/TextInput';
import { Select } from '../components/Select';
import { Badge } from '../components/Badge';
import { WeekdayPicker } from '../components/WeekdayPicker';
import { CitySearchInput } from '../components/CitySearchInput';
import { CityPicker } from '../components/CityPicker';
import { PickupField } from '../components/PickupField';
import { getWebApp, hapticError, hapticRigid, hapticSelection, hapticSuccess } from '../lib/telegram';

type Step = 0 | 1 | 2 | 3 | 4 | 5;
const STEPS_COUNT = 6;
const DRAFT_KEY = 'waylo_publish_draft';

interface Form {
  routeMode: 'preset' | 'custom';
  routeId: string;
  customOriginId: string;
  customDestinationId: string;
  viaCityIds: string[];
  isRecurring: boolean;
  weekdayMask: number; // only when isRecurring
  date: string;
  time: string;
  flex: number;
  seats: number;
  cost: number;
  approvalMode: 'manual' | 'auto';
  smoking: boolean;
  music: boolean;
  pets: boolean;
  luggage: 'small' | 'medium' | 'large';
  womenOnly: boolean;
  allowPartial: boolean;
  notes: string;
  pickupTitle: string;
  pickupLat: number | null;
  pickupLng: number | null;
}

/** Default form values. Vibes pre-fills overlay these. */
function blankForm(): Form {
  // Default to tomorrow at 08:00 local — server requires departure > now+5min.
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return {
    routeMode: 'preset',
    routeId: '',
    customOriginId: '',
    customDestinationId: '',
    viaCityIds: [],
    isRecurring: false,
    weekdayMask: 0,
    date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
    time: '08:00',
    flex: 10,
    seats: 3,
    cost: 0,
    approvalMode: 'manual',
    smoking: false,
    music: true,
    pets: false,
    luggage: 'medium',
    womenOnly: false,
    allowPartial: false,
    notes: '',
    pickupTitle: '',
    pickupLat: null,
    pickupLng: null,
  };
}

export function PublishPage(): JSX.Element {
  const { navigate } = useRouter();
  const { me } = useSession();
  const { t, locale } = useT();
  const [step, setStep] = useState<Step>(0);
  // Initialize form with: last-saved draft from localStorage (if any) →
  // overlaid by the user's vibes → blank defaults. Draft survives accidental
  // app backgrounding / WebView reloads inside Telegram.
  const [form, setForm] = useState<Form>(() => {
    const base = blankForm();
    const v = me?.vibes;
    const withVibes: Form = v
      ? {
          ...base,
          music: v.music ?? base.music,
          smoking: v.smoking ?? base.smoking,
          pets: v.pets ?? base.pets,
          luggage: v.luggage ?? base.luggage,
          womenOnly: v.womenOnly ?? base.womenOnly,
        }
      : base;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return withVibes;
      const parsed = JSON.parse(raw) as Partial<Form>;
      return { ...withVibes, ...parsed };
    } catch {
      return withVibes;
    }
  });

  // Debounced draft save. Skips first render and skips after publish success.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
      } catch {
        /* quota or private mode — silently ignore */
      }
    }, 400);
    return () => window.clearTimeout(handle);
  }, [form]);

  const [error, setError] = useState<string | null>(null);
  const [resultMsg, setResultMsg] = useState<string | null>(null);

  const routesQ = useQuery({ queryKey: ['routes'], queryFn: listRoutes });
  const country = localeCountry(locale);
  // Selected city objects for the custom route (search is server-side now, so
  // we keep the chosen objects for display/review instead of a full list).
  const [customOrigin, setCustomOrigin] = useState<CityDto | null>(null);
  const [customDest, setCustomDest] = useState<CityDto | null>(null);
  const isCustom = form.routeMode === 'custom';
  const selectedRoute = useMemo(
    () => (isCustom ? null : (routesQ.data?.items.find((r) => r.id === form.routeId) ?? null)),
    [isCustom, routesQ.data, form.routeId],
  );
  const nameKey: 'nameSr' | 'nameEn' = locale === 'en' ? 'nameEn' : 'nameSr';

  // Pickup map centre = the chosen origin city (fallback Belgrade). Dark map
  // style follows the Telegram colour scheme.
  const originCity = isCustom ? customOrigin : selectedRoute?.origin;
  const pickupCenter: [number, number] = [originCity?.lat ?? 44.7866, originCity?.lng ?? 20.4489];
  const isDark = getWebApp()?.colorScheme === 'dark';

  const routeEndpointIds = useMemo(
    () =>
      isCustom
        ? [form.customOriginId, form.customDestinationId].filter((x): x is string => Boolean(x))
        : selectedRoute
          ? [selectedRoute.originCityId, selectedRoute.destinationCityId]
          : [],
    [isCustom, form.customOriginId, form.customDestinationId, selectedRoute],
  );

  useEffect(() => {
    if (!isCustom && !form.routeId && routesQ.data && routesQ.data.items.length) {
      setForm((f) => ({ ...f, routeId: routesQ.data!.items[0]!.id }));
    }
  }, [isCustom, routesQ.data, form.routeId]);

  // Auto-fill cost once per route change. The previous `form.cost === 0`
  // gate re-fired every time the user backspaced to clear the field —
  // which racily clobbered the typed value back to the reference. We
  // remember which route id we last auto-filled for.
  const lastAutoFilledFor = useRef<string | null>(null);
  useEffect(() => {
    if (selectedRoute && lastAutoFilledFor.current !== selectedRoute.id) {
      lastAutoFilledFor.current = selectedRoute.id;
      setForm((f) => ({ ...f, cost: Math.round(selectedRoute.referenceCost) }));
    }
  }, [selectedRoute]);

  // Hard-cap "wall" haptic on first cost crossing into reject band. Fires
  // once per crossing, not on every keystroke past the cap.
  const wasOverpriced = useRef(false);
  useEffect(() => {
    const r = selectedRoute ? form.cost / Math.max(1, selectedRoute.referenceCost) : 0;
    const over = r > 1.5;
    if (over && !wasOverpriced.current) {
      hapticRigid();
    }
    wasOverpriced.current = over;
  }, [form.cost, selectedRoute]);

  const publish = useMutation({
    mutationFn: async () => {
      if (isCustom) {
        if (
          !form.customOriginId ||
          !form.customDestinationId ||
          form.customOriginId === form.customDestinationId
        ) {
          throw new Error('route required');
        }
      } else if (!selectedRoute) {
        throw new Error('route required');
      }
      const prefs = {
        smokingAllowed: form.smoking,
        musicAllowed: form.music,
        petsAllowed: form.pets,
        luggageMax: form.luggage,
        womenOnly: form.womenOnly,
      };
      const viaCityIds = form.viaCityIds.length > 0 ? form.viaCityIds : undefined;
      // Recurring templates only support predefined routes (the materializer
      // references a routeId). Custom routes always publish as a one-off.
      if (form.isRecurring && !isCustom) {
        const r = await createTemplateApi({
          routeId: form.routeId,
          weekdayMask: form.weekdayMask,
          departureTime: form.time,
          flexibilityMinutes: form.flex,
          seatsTotal: form.seats,
          proposedCost: form.cost,
          approvalMode: form.approvalMode,
          preferences: prefs,
          pickupNotes: form.notes || undefined,
          viaCityIds,
        });
        return { kind: 'template' as const, id: r.id };
      }
      // For one-off rides: build departureAt in Europe/Belgrade local time, not UTC.
      // The user's "08:00" means 08:00 Belgrade, which is 06:00 UTC summer or 07:00 winter.
      const departureAt = belgradeLocalToUtc(form.date, form.time).toISOString();
      const routeFields = isCustom
        ? { originCityId: form.customOriginId, destinationCityId: form.customDestinationId }
        : { routeId: form.routeId };
      const r = await publishRideApi({
        ...routeFields,
        departureAt,
        flexibilityMinutes: form.flex,
        seatsTotal: form.seats,
        proposedCost: form.cost,
        approvalMode: form.approvalMode,
        preferences: prefs,
        pickupNotes: form.notes || undefined,
        pickupTitle: form.pickupTitle.trim() || undefined,
        pickupLat: form.pickupLat ?? undefined,
        pickupLng: form.pickupLng ?? undefined,
        viaCityIds,
        allowPartial: viaCityIds ? form.allowPartial : undefined,
      });
      return { kind: 'ride' as const, ...r };
    },
    onSuccess: (r) => {
      hapticSuccess();
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch {
        /* ignore */
      }
      if (r.kind === 'template') {
        setResultMsg(t('publish.submitted.recurring'));
      } else {
        setResultMsg(r.flagged ? t('publish.submitted.review') : t('publish.submitted.live'));
      }
    },
    onError: (e: Error) => {
      hapticError();
      setError(translateError(t, e));
    },
  });

  if (!me?.isDriver) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6">
        <BackButton />
        <Card className="mt-4 space-y-3">
          <CardTitle>{t('publish.driverModeFirst.t')}</CardTitle>
          <CardHint>{t('publish.driverModeFirst.b')}</CardHint>
          <button
            type="button"
            onClick={() => navigate('/profile')}
            className="rounded-input bg-brand-green px-4 py-2 text-sm font-semibold text-white"
          >
            {t('publish.openProfile')}
          </button>
        </Card>
      </div>
    );
  }

  if (resultMsg) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center px-4 py-6 text-center">
        <BrandMark size={72} className="animate-pop mb-4" />
        <h1
          className="animate-card-in text-2xl font-bold text-tg-text"
          style={{ animationDelay: '160ms' }}
        >
          {t('publish.submitted.title')}
        </h1>
        <p className="animate-card-in mt-2 text-tg-hint" style={{ animationDelay: '260ms' }}>
          {resultMsg}
        </p>
        <button
          type="button"
          onClick={() => navigate('/my/rides')}
          className="animate-card-in mt-6 rounded-input bg-brand-green px-4 py-2 text-sm font-semibold text-white"
          style={{ animationDelay: '360ms' }}
        >
          {t('publish.seeMyRides')}
        </button>
      </div>
    );
  }

  const ratio = selectedRoute ? form.cost / Math.max(1, selectedRoute.referenceCost) : 1;
  const overpriced = ratio > 1.5;
  const warnPriced = ratio > 1.2 && ratio <= 1.5;

  const handleNext = (): void => {
    setError(null);
    if (step < STEPS_COUNT - 1) {
      // Route step must be complete before advancing — otherwise an unselected
      // custom city slipped through to publish as a confusing "route required".
      if (step === 0) {
        const routeIncomplete = isCustom
          ? !form.customOriginId ||
            !form.customDestinationId ||
            form.customOriginId === form.customDestinationId
          : !form.routeId;
        if (routeIncomplete) {
          hapticError();
          setError(t('publish.route.incomplete'));
          return;
        }
      }
      if (step === 1 && form.isRecurring && form.weekdayMask === 0) {
        return;
      }
      // Overpriced no longer blocks — autonomous platform. Driver was already
      // shown the warning badge in step 3 and the red advisory card.
      setStep((step + 1) as Step);
    } else {
      publish.mutate();
    }
  };

  const handleBack = (): void => {
    if (step === 0) navigate('/');
    else setStep((step - 1) as Step);
  };

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6">
      <BackButton onBack={handleBack} />
      <p className="mb-1 text-center text-xs font-medium text-tg-hint">
        {t('publish.stepCount', { n: step + 1, total: STEPS_COUNT })}
      </p>
      <ProgressDots step={step} onJump={(i) => setStep(i)} />

      <div key={step} className="mt-4 flex-1 animate-step-in">
        {step === 0 && (
          <section className="space-y-3">
            <h2 className="text-xl font-bold text-tg-text">{t('publish.step.route.t')}</h2>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  hapticSelection();
                  setForm({ ...form, routeMode: 'preset', viaCityIds: [] });
                }}
                className={
                  'flex-1 rounded-input py-2.5 text-sm font-semibold transition ' +
                  (!isCustom ? 'bg-[var(--border-medium)] text-tg-text' : 'bg-tg-bg-secondary text-tg-hint')
                }
              >
                {t('publish.route.mode.preset')}
              </button>
              <button
                type="button"
                onClick={() => {
                  hapticSelection();
                  setForm({ ...form, routeMode: 'custom', cost: 0, viaCityIds: [] });
                }}
                className={
                  'flex-1 rounded-input py-2.5 text-sm font-semibold transition ' +
                  (isCustom ? 'bg-[var(--border-medium)] text-tg-text' : 'bg-tg-bg-secondary text-tg-hint')
                }
              >
                {t('publish.route.mode.custom')}
              </button>
            </div>

            {!isCustom ? (
              <Select
                id="route"
                options={(routesQ.data?.items ?? []).map((r) => ({
                  value: r.id,
                  label: `${r.origin[nameKey]} → ${r.destination[nameKey]}`,
                }))}
                value={form.routeId}
                onChange={(e) =>
                  setForm({ ...form, routeId: e.target.value, cost: 0, viaCityIds: [] })
                }
              />
            ) : (
              <div className="space-y-3">
                <CityPicker
                  value={form.customOriginId}
                  selectedCity={customOrigin}
                  onChange={(id, c) => {
                    setCustomOrigin(c);
                    setForm({ ...form, customOriginId: id, viaCityIds: [] });
                  }}
                  country={country}
                  nameKey={nameKey}
                  label={t('home.from')}
                  placeholder={t('publish.step.route.via.placeholder')}
                  emptyText={t('publish.step.route.via.empty')}
                  excludeId={form.customDestinationId || undefined}
                />
                <CityPicker
                  value={form.customDestinationId}
                  selectedCity={customDest}
                  onChange={(id, c) => {
                    setCustomDest(c);
                    setForm({ ...form, customDestinationId: id, viaCityIds: [] });
                  }}
                  country={country}
                  nameKey={nameKey}
                  label={t('home.to')}
                  placeholder={t('publish.step.route.via.placeholder')}
                  emptyText={t('publish.step.route.via.empty')}
                  excludeId={form.customOriginId || undefined}
                />
                <CardHint>{t('publish.route.custom.hint')}</CardHint>
              </div>
            )}

            {(selectedRoute || (isCustom && form.customOriginId && form.customDestinationId)) && (
                <Card className="space-y-2">
                  <CardTitle>{t('publish.step.route.via.t')}</CardTitle>
                  <CardHint>{t('publish.step.route.via.hint')}</CardHint>
                  <CitySearchInput
                    country={country}
                    selectedIds={form.viaCityIds}
                    onChange={(next) => setForm({ ...form, viaCityIds: next })}
                    excludeIds={routeEndpointIds}
                    maxSelected={8}
                    nameKey={nameKey}
                    placeholder={t('publish.step.route.via.placeholder')}
                    emptyText={t('publish.step.route.via.empty')}
                  />
                </Card>
              )}
          </section>
        )}

        {step === 1 && (
          <section className="space-y-3">
            <h2 className="text-xl font-bold text-tg-text">{t('publish.step.when.t')}</h2>

            {/* Recurring templates need a predefined routeId; hide for custom routes. */}
            {!isCustom && (
              <Card className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle>{t('publish.step.repeat.t')}</CardTitle>
                    <CardHint>{t('publish.step.repeat.hint')}</CardHint>
                  </div>
                  <input
                    type="checkbox"
                    checked={form.isRecurring}
                    onChange={(e) =>
                      setForm({ ...form, isRecurring: e.target.checked, weekdayMask: 0 })
                    }
                    className="h-6 w-6 flex-none accent-brand-green"
                  />
                </div>
                {form.isRecurring && (
                  <>
                    <WeekdayPicker
                      mask={form.weekdayMask}
                      onChange={(m) => setForm({ ...form, weekdayMask: m })}
                    />
                    {form.weekdayMask === 0 && (
                      <p className="text-xs text-destructive">
                        {t('publish.step.repeat.atLeastOne')}
                      </p>
                    )}
                  </>
                )}
              </Card>
            )}

            {!form.isRecurring && (
              <TextInput
                id="date"
                label={t('publish.step.when.date')}
                type="date"
                min={(() => {
                  const d = new Date();
                  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                })()}
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            )}
            <TextInput
              id="time"
              label={t('publish.step.when.time')}
              type="time"
              step={300}
              value={form.time}
              onChange={(e) => setForm({ ...form, time: e.target.value })}
            />
            <Select
              id="flex"
              label={t('publish.step.when.flex')}
              options={[
                { value: '0', label: t('publish.step.when.flexExact') },
                { value: '10', label: t('publish.step.when.flex10') },
                { value: '30', label: t('publish.step.when.flex30') },
                { value: '60', label: t('publish.step.when.flex60') },
              ]}
              value={String(form.flex)}
              onChange={(e) => setForm({ ...form, flex: Number(e.target.value) })}
            />
          </section>
        )}

        {step === 2 && (
          <section className="space-y-3">
            <h2 className="text-xl font-bold text-tg-text">{t('publish.step.seats.t')}</h2>
            <Select
              id="seats"
              options={[1, 2, 3, 4].map((n) => ({
                value: String(n),
                label: `${n} ${n === 1 ? t('common.seat_one') : t('common.seat_other')}`,
              }))}
              value={String(form.seats)}
              onChange={(e) => setForm({ ...form, seats: Number(e.target.value) })}
            />
            <Card>
              <CardTitle>{t('publish.step.approval.t')}</CardTitle>
              <CardHint>{t('publish.step.approval.hint')}</CardHint>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {(['manual', 'auto'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => {
                      if (form.approvalMode !== m) hapticSelection();
                      setForm({ ...form, approvalMode: m });
                    }}
                    className={
                      'rounded-input border px-3 py-2 text-sm font-medium ' +
                      (form.approvalMode === m
                        ? 'border-transparent bg-[var(--border-medium)] font-semibold text-tg-text'
                        : 'border-[var(--border-light)] text-tg-hint')
                    }
                  >
                    {m === 'manual'
                      ? t('publish.step.approval.manual')
                      : t('publish.step.approval.auto')}
                  </button>
                ))}
              </div>
            </Card>
          </section>
        )}

        {step === 3 && (
          <section className="space-y-3">
            <h2 className="text-xl font-bold text-tg-text">{t('publish.step.cost.t')}</h2>
            <TextInput
              id="cost"
              label={t('publish.step.cost.label', { currency: selectedRoute?.currency ?? 'RSD' })}
              type="number"
              min={1}
              value={String(form.cost)}
              onChange={(e) => setForm({ ...form, cost: Number(e.target.value) })}
            />
            {selectedRoute && (
              <Card>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('publish.step.cost.refTitle')}</CardTitle>
                  <Badge tone={overpriced ? 'danger' : warnPriced ? 'warning' : 'success'}>
                    {ratio.toFixed(2)}× ref
                  </Badge>
                </div>
                <CardHint>
                  {t('publish.step.cost.refBody', {
                    ref: Math.round(selectedRoute.referenceCost).toLocaleString(),
                    currency: selectedRoute.currency,
                  })}
                </CardHint>
                {warnPriced && (
                  <p className="mt-2 rounded-input bg-[var(--warning)]/10 p-2 text-xs text-[var(--warning)]">
                    {t('publish.step.cost.warn')}
                  </p>
                )}
                {overpriced && (
                  <p className="mt-2 rounded-input bg-destructive/10 p-2 text-xs text-destructive">
                    {t('publish.step.cost.reject')}
                  </p>
                )}
              </Card>
            )}
          </section>
        )}

        {step === 4 && (
          <section className="space-y-3">
            <h2 className="text-xl font-bold text-tg-text">{t('publish.step.prefs.t')}</h2>
            <Toggle
              label={t('publish.step.prefs.music')}
              value={form.music}
              onChange={(v) => {
                hapticSelection();
                setForm({ ...form, music: v });
              }}
            />
            <Toggle
              label={t('publish.step.prefs.smoking')}
              value={form.smoking}
              onChange={(v) => {
                hapticSelection();
                setForm({ ...form, smoking: v });
              }}
            />
            <Toggle
              label={t('publish.step.prefs.pets')}
              value={form.pets}
              onChange={(v) => {
                hapticSelection();
                setForm({ ...form, pets: v });
              }}
            />
            <Toggle
              label={t('publish.step.prefs.women')}
              value={form.womenOnly}
              onChange={(v) => {
                hapticSelection();
                setForm({ ...form, womenOnly: v });
              }}
            />
            <Select
              id="luggage"
              label={t('publish.step.prefs.luggage')}
              options={[
                { value: 'small', label: t('publish.step.prefs.luggage.small') },
                { value: 'medium', label: t('publish.step.prefs.luggage.medium') },
                { value: 'large', label: t('publish.step.prefs.luggage.large') },
              ]}
              value={form.luggage}
              onChange={(e) => setForm({ ...form, luggage: e.target.value as Form['luggage'] })}
            />
            {form.viaCityIds.length > 0 && (
              <Card>
                <Toggle
                  label={t('segment.allow')}
                  value={form.allowPartial}
                  onChange={(v) => {
                    hapticSelection();
                    setForm({ ...form, allowPartial: v });
                  }}
                />
                <CardHint>{t('segment.allow.hint')}</CardHint>
              </Card>
            )}
          </section>
        )}

        {step === 5 && (
          <section className="space-y-3">
            <h2 className="text-xl font-bold text-tg-text">{t('publish.step.notes.t')}</h2>
            <TextArea
              id="notes"
              label={t('publish.step.notes.label')}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder={t('publish.step.notes.ph')}
              maxLength={280}
            />
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-tg-text">{t('pickup.step.t')}</h3>
              <PickupField
                value={{ title: form.pickupTitle, lat: form.pickupLat, lng: form.pickupLng }}
                centerLat={pickupCenter[0]}
                centerLng={pickupCenter[1]}
                dark={isDark}
                onChange={(v) =>
                  setForm({ ...form, pickupTitle: v.title, pickupLat: v.lat, pickupLng: v.lng })
                }
              />
            </div>
            <Card className="mt-3 space-y-2">
              <CardTitle>{t('publish.step.review.t')}</CardTitle>
              <SummaryRow
                k={t('publish.step.review.route')}
                v={
                  selectedRoute
                    ? `${selectedRoute.origin[nameKey]} → ${selectedRoute.destination[nameKey]}`
                    : customOrigin && customDest
                      ? `${customOrigin[nameKey]} → ${customDest[nameKey]}`
                      : '—'
                }
              />
              <SummaryRow
                k={t('publish.step.review.departure')}
                v={`${humanDate(form.date, locale, t)} ${form.time} (± ${form.flex}m)`}
              />
              <SummaryRow k={t('publish.step.review.seats')} v={String(form.seats)} />
              <SummaryRow
                k={t('publish.step.review.cost')}
                v={`${form.cost.toLocaleString()} ${selectedRoute?.currency ?? 'RSD'} / ${t('common.perSeat')}`}
              />
              <SummaryRow
                k={t('publish.step.review.approval')}
                v={
                  form.approvalMode === 'manual'
                    ? t('publish.step.approval.manual')
                    : t('publish.step.approval.auto')
                }
              />
            </Card>
          </section>
        )}
      </div>

      {error && (
        <p className="mb-2 rounded-card bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
      )}

      <MainButton
        text={
          step < STEPS_COUNT - 1
            ? t('publish.cta.next')
            : publish.isPending
              ? t('publish.cta.publishing')
              : t('publish.cta.publish')
        }
        loading={publish.isPending}
        disabled={!selectedRoute}
        onClick={handleNext}
      />
    </div>
  );
}

function ProgressDots({ step, onJump }: { step: Step; onJump: (i: Step) => void }): JSX.Element {
  return (
    <div className="flex items-center justify-center gap-1.5">
      {Array.from({ length: STEPS_COUNT }).map((_, i) => {
        const isCompleted = i < step;
        const isActive = i === step;
        return (
          <button
            key={i}
            type="button"
            aria-label={`Step ${i + 1}`}
            aria-current={isActive ? 'step' : undefined}
            disabled={!isCompleted && !isActive}
            onClick={() => isCompleted && onJump(i as Step)}
            className={
              'h-3 rounded-pill transition-[width,background-color] duration-200 ease-out ' +
              (isActive
                ? 'w-8 bg-tg-text'
                : isCompleted
                  ? 'w-3 bg-[var(--border-strong)] cursor-pointer'
                  : 'w-3 bg-[var(--border-medium)] cursor-default')
            }
          />
        );
      })}
    </div>
  );
}

function Toggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <label className="flex items-center justify-between rounded-card bg-tg-bg-secondary p-3">
      <span className="text-tg-text">{label}</span>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="h-5 w-5 accent-brand-green"
      />
    </label>
  );
}

function SummaryRow({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-tg-hint">{k}</span>
      <span className="text-tg-text">{v}</span>
    </div>
  );
}

/**
 * Converts a Belgrade-local YYYY-MM-DD + HH:MM string pair into a proper UTC Date.
 * Belgrade is UTC+1 (CET) winter, UTC+2 (CEST) summer (last Sun of Mar → last Sun of Oct).
 * Fixes the long-standing bug where `new Date('...Z')` treated local time as UTC.
 */
function belgradeLocalToUtc(date: string, time: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  // Construct as UTC, then subtract the Belgrade offset to get the true UTC instant.
  const tentativeUtc = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0, 0, 0);
  const year = y ?? 1970;
  // Compute Belgrade's last-Sunday-of-March DST start (01:00 UTC), last Sunday of October DST end.
  const dstStart = (() => {
    const x = new Date(Date.UTC(year, 3, 0)); // last day of March
    const last = x.getUTCDate() - x.getUTCDay();
    return Date.UTC(year, 2, last, 1);
  })();
  const dstEnd = (() => {
    const x = new Date(Date.UTC(year, 10, 0)); // last day of October
    const last = x.getUTCDate() - x.getUTCDay();
    return Date.UTC(year, 9, last, 1);
  })();
  const offsetHours = tentativeUtc >= dstStart && tentativeUtc < dstEnd ? 2 : 1;
  return new Date(tentativeUtc - offsetHours * 60 * 60 * 1000);
}
